import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL as API_URL } from '../config/api';
import { RefreshCw, Gauge, AlertTriangle } from 'lucide-react';

/**
 * Χρόνοι εκτύπωσης ανά εκτυπωτή — ΜΟΝΟ ΑΝΑΓΝΩΣΗ.
 *
 * Τα νούμερα έρχονται από τις μετρήσεις που στέλνει ο print agent 2.0.0+ μαζί με
 * το print-complete (print_jobs.timings). Κατάστημα με παλιό agent ΔΕΝ εμφανίζεται
 * καθόλου — δεν δείχνουμε μηδενικά, γιατί το «0 ms» διαβάζεται ψευδώς ως «γρήγορο».
 * Χωρίς αυτόν τον πίνακα καμία βελτιστοποίηση δεν είναι μετρήσιμη: είναι η πρώτη
 * οθόνη που κοιτάμε πριν και μετά από κάθε αλλαγή σε PC καταστήματος.
 */

interface TimingRow {
  printer_id: number;
  printer_name: string | null;
  job_type: string | null;
  method: string | null;
  conn: string | null;
  agent_version: string | null;
  jobs: number | string | null;
  total_p50: number | string | null;
  total_p90: number | string | null;
  render_p50: number | string | null;
  raster_p50: number | string | null;
  dispatch_p50: number | string | null;
  bytes_avg: number | string | null;
  height_avg: number | string | null;
}

interface ServerRow {
  printer_id?: number;
  printer_name?: string | null;
  jobs?: number | string | null;
  deliver_p50?: number | string | null;   // created_at -> queued_at (πλευρά server)
  deliver_p90?: number | string | null;
  agent_p50?: number | string | null;     // queued_at -> completed_at (πλευρά agent)
  agent_p90?: number | string | null;
}

interface Props {
  storeId: number;
  token: string;
  /**
   * Από πού διαβάζει (χωρίς ?days): από 25/10/2026 το support, /api/support/stores/:id/printer-timings — η σελίδα
   * «Εκτυπωτές» του καταστήματος δεν δείχνει πια τις επιδόσεις. Προεπιλογή η διαδρομή του καταστήματος.
   */
  path?: string;
}

// Η pg επιστρέφει numeric ως string — κάθε τιμή περνά από εδώ πριν μπει σε πράξη.
const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const PrinterTimings: React.FC<Props> = ({ storeId, token, path }) => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const [days, setDays] = useState<number>(7);
  const [rows, setRows] = useState<TimingRow[]>([]);
  const [serverRows, setServerRows] = useState<ServerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const nf = useCallback((value: number, digits: number) =>
    new Intl.NumberFormat(i18n.language || 'el', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(value), [i18n.language]);

  // Κάτω από 1 δευτ. σε ms (εκεί κρίνονται οι φάσεις), αλλιώς δευτερόλεπτα.
  const ms = useCallback((v: number | string | null | undefined): string => {
    const n = num(v);
    if (n === null) return '—';
    return n < 1000 ? `${nf(n, 0)} ms` : `${nf(n / 1000, 2)} s`;
  }, [nf]);

  const kb = useCallback((v: number | string | null | undefined): string => {
    const n = num(v);
    if (n === null) return '—';
    return n < 1024 ? `${nf(n, 0)} B` : `${nf(n / 1024, 0)} KB`;
  }, [nf]);

  // 203 dpi = 8 dots/mm: τα px ύψους γίνονται χιλιοστά χαρτιού διαιρώντας με 8.
  const mm = useCallback((v: number | string | null | undefined): string => {
    const n = num(v);
    if (n === null || n === 0) return '—';
    return `${nf(n / 8, 0)} mm`;
  }, [nf]);

  const fetchTimings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}${path || `/api/stores/${storeId}/printers/timings`}?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        // Παλιός server: η διαδρομή δεν υπάρχει ακόμη και το /printers/:printerId
        // πιάνει το «timings» ως id. Δεν είναι σφάλμα του χρήστη.
        setUnavailable(true);
        setRows([]);
        setServerRows([]);
        return;
      }
      const data = await res.json();
      // Σχήμα server: { days, phases: [...], delivery: [...] }. Κρατάμε και τη
      // σκέτη λίστα ως εναλλακτική, για να μην αδειάσει ο πίνακας αν αλλάξει.
      const payload: any = Array.isArray(data) ? { phases: data } : (data || {});
      setRows(Array.isArray(payload.phases) ? payload.phases : (Array.isArray(payload.rows) ? payload.rows : []));
      setServerRows(Array.isArray(payload.delivery) ? payload.delivery : []);
      setUnavailable(false);
    } catch {
      setUnavailable(true);
      setRows([]);
      setServerRows([]);
    } finally {
      setLoading(false);
    }
  }, [storeId, token, days, path]);

  useEffect(() => { fetchTimings(); }, [fetchTimings]);

  const methodBadge = (method: string | null) => {
    const m = method || 'text';
    const cls = m === 'image'
      ? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300'
      : m === 'hybrid'
        ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
        : 'bg-surface-3 text-t2';
    return <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{m}</span>;
  };

  const agentBadge = (version: string | null) => {
    if (!version) return <span className="text-xs text-t4">—</span>;
    const outdated = (parseInt(version.split('.')[0], 10) || 0) < 2;
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs ${outdated
          ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
          : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'}`}
        title={outdated ? t('printer.agentOutdated') : undefined}
      >
        v{version}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-400">
            <Gauge className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium text-t1">{t('printer.timingsTitle')}</h3>
            <p className="text-sm text-t3">{t('printer.timingsSubtitle')}</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs text-t3 mb-1">{t('printer.timingsDays')}</label>
            <select
              className="px-3 py-2 bg-surface-2 border border-line-strong rounded-lg text-t1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              value={days}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDays(parseInt(e.target.value, 10))}
            >
              <option value={1}>{t('printer.timingsDays1')}</option>
              <option value={7}>{t('printer.timingsDays7')}</option>
              <option value={30}>{t('printer.timingsDays30')}</option>
            </select>
          </div>
          <button
            onClick={fetchTimings}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 border border-line-strong text-t2 hover:bg-surface-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('printer.refresh')}
          </button>
        </div>
      </div>

      {unavailable && (
        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{t('printer.timingsUnavailable')}</span>
        </div>
      )}

      {!unavailable && !loading && rows.length === 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl p-8 text-center text-sm text-t3">
          {t('printer.timingsEmpty')}
        </div>
      )}

      {!unavailable && rows.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-t3 border-b border-line">
                <th className="px-3 py-2 font-medium">{t('printer.timingsColPrinter')}</th>
                <th className="px-3 py-2 font-medium">{t('printer.timingsColJobType')}</th>
                <th className="px-3 py-2 font-medium">{t('printer.timingsColMethod')}</th>
                <th className="px-3 py-2 font-medium">{t('printer.timingsColConn')}</th>
                <th className="px-3 py-2 font-medium">{t('printer.timingsColAgent')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColJobs')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColTotalP50')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColTotalP90')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColRender')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColRaster')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColDispatch')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColBytes')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColLength')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.printer_id}-${r.job_type}-${r.method}-${r.conn}-${i}`} className="border-b border-line/60 last:border-0">
                  <td className="px-3 py-2 text-t1">{r.printer_name || `#${r.printer_id}`}</td>
                  <td className="px-3 py-2 text-t3">{r.job_type || '—'}</td>
                  <td className="px-3 py-2">{methodBadge(r.method)}</td>
                  <td className="px-3 py-2 text-t3">{r.conn && r.conn !== '?' ? r.conn : '—'}</td>
                  <td className="px-3 py-2">{agentBadge(r.agent_version)}</td>
                  <td className="px-3 py-2 text-right text-t3">{num(r.jobs) ?? '—'}</td>
                  <td className="px-3 py-2 text-right text-t1 font-medium">{ms(r.total_p50)}</td>
                  <td className="px-3 py-2 text-right text-t2">{ms(r.total_p90)}</td>
                  <td className="px-3 py-2 text-right text-t3">{ms(r.render_p50)}</td>
                  <td className="px-3 py-2 text-right text-t3">{ms(r.raster_p50)}</td>
                  <td className="px-3 py-2 text-right text-t3">{ms(r.dispatch_p50)}</td>
                  <td className="px-3 py-2 text-right text-t3">{kb(r.bytes_avg)}</td>
                  <td className="px-3 py-2 text-right text-t3">{mm(r.height_avg)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!unavailable && serverRows.length > 0 && (
        <div className="space-y-2">
          <div>
            <h4 className="font-medium text-t2">{t('printer.timingsServerTitle')}</h4>
            <p className="text-xs text-t3">{t('printer.timingsServerSubtitle')}</p>
          </div>
          <div className="bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-t3 border-b border-line">
                  <th className="px-3 py-2 font-medium">{t('printer.timingsColPrinter')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColJobs')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColDeliverP50')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColDeliverP90')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColAgentP50')}</th>
                  <th className="px-3 py-2 font-medium text-right">{t('printer.timingsColAgentP90')}</th>
                </tr>
              </thead>
              <tbody>
                {serverRows.map((r, i) => (
                  <tr key={`srv-${r.printer_id ?? 'x'}-${i}`} className="border-b border-line/60 last:border-0">
                    <td className="px-3 py-2 text-t1">{r.printer_name || (r.printer_id ? `#${r.printer_id}` : '—')}</td>
                    <td className="px-3 py-2 text-right text-t3">{num(r.jobs) ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-t2">{ms(r.deliver_p50)}</td>
                    <td className="px-3 py-2 text-right text-t3">{ms(r.deliver_p90)}</td>
                    <td className="px-3 py-2 text-right text-t2">{ms(r.agent_p50)}</td>
                    <td className="px-3 py-2 text-right text-t3">{ms(r.agent_p90)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrinterTimings;
